import { Button } from 'src/components/ui/button';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { Controller, useForm } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { SalesRolePopup } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/RoleDescriptionField/SalesRoleField/SalesRolePopup/SalesRolePopup';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { ROLE_OPTIONS } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep4/OnboardingStep4PrompBuilder/OnboardingStep4PrompBuilder';
import {
  useCreateConfigurationSettingsMutation,
  useGetProjectByIdQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useGetAccountsQuery } from 'src/services/accountsService';
import { formResolver } from 'src/utils/formResolver';
import * as yup from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { API_VERSION } from 'src/constants/api-routes';
import { getCreateProjectStepPath } from 'src/pages/ProjectPage/CreateProjectSteps/createProjectStepPath';
import type {
  ConfigurationSettings,
  ConfigurationTemplateFields,
} from 'src/services/configurationService';
import type {
  PersonaInputData,
  PromptBuilderFormValues,
} from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { getApiVersion } from 'src/utils/apiVersion';

export const CreateProjectPromptBuilderStep4 = ({
  activeSettings,
  defaultTemplateFields,
  inputData,
  formFieldsObject,
}: {
  activeSettings: ConfigurationSettings | null;
  defaultTemplateFields?: ConfigurationTemplateFields;
  inputData: PersonaInputData | null;
  formFieldsObject: ConfigurationFormFieldsObject;
}) => {
  const customInputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const dispatch = useAppDispatch();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { data: account } = useGetAccountsQuery();
  const [
    createConfigurationSettings,
    { data: createConfigurationSettingsData, isLoading: isLoadingCreateConfigurationSettings },
  ] = useCreateConfigurationSettingsMutation();
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;

  useLoaderWhile(isLoadingCreateConfigurationSettings, isLoadingUpdateConfigurationSettings);

  useEffect(() => {
    if (account?.company_name) {
      setValue('pf_company_name', account.company_name);
    }
  }, [account]);

  useEffect(() => {
    if (currentProject?.name) {
      setValue('pf_ai_name', currentProject.name);
    }
  }, [currentProject]);

  useEffect(() => {
    if (inputData) {
      reset({
        pf_ai_name: inputData ? inputData.pf_ai_name : currentProject?.name || '',
        pf_company_name: inputData ? inputData.pf_company_name : '',
        pf_company_description: inputData ? inputData.pf_company_description : '',
        pf_role: inputData?.pf_role || formFieldsObject?.pf_role?.default_value?.value || 'support',
        pf_role_custom: inputData?.pf_role_custom || '',
        pf_cta: inputData?.pf_cta || null,
        pf_icp: inputData?.pf_icp || null,
      });
    }
  }, [inputData]);

  useEffect(() => {
    if (createConfigurationSettingsData || updateConfigurationSettingsData) {
      if (createConfigurationSettingsData) {
        dispatch(
          setCreationFlow({
            id: id,
            step: step,
            promptBuilderSettingsId: createConfigurationSettingsData.id,
          }),
        );
      }

      next();
    }
  }, [createConfigurationSettingsData, updateConfigurationSettingsData]);

  const { handleSubmit, control, reset, watch, setError, setValue } =
    useForm<PromptBuilderFormValues>({
      defaultValues: {
        pf_ai_name: inputData ? inputData.pf_ai_name : currentProject?.name || '',
        pf_company_name: inputData ? inputData.pf_company_name : '',
        pf_company_description: inputData ? inputData.pf_company_description : '',
        pf_role: inputData?.pf_role || formFieldsObject?.pf_role?.default_value?.value || 'support',
        pf_role_custom: inputData?.pf_role_custom || '',
        pf_cta: inputData?.pf_cta || null,
        pf_icp: inputData?.pf_icp || null,
      },
      resolver: formResolver(
        yup.lazy((values) => {
          const baseSchema = {
            pf_role: stringScheme({
              required: formFieldsObject?.pf_role?.is_required || false,
            }).label('Role'),
            pf_role_custom: stringScheme({
              required: values.pf_role === 'custom',
            }).label('Role - Custom'),
          };

          return yup.object(baseSchema);
        }),
      ),
    });

  const role = watch('pf_role');
  const custom = watch('pf_role_custom');
  const cta = watch('pf_cta');
  const icp = watch('pf_icp');

  useEffect(() => {
    if (role && custom && role !== 'custom') {
      setValue('pf_role_custom', '');
    }
  }, [role, custom]);

  const submit = (data: PromptBuilderFormValues) => {
    if (!isV2 && role === 'sales' && (!cta || !icp)) {
      return setError('pf_role', {
        message: 'Sales role setup incomplete',
      });
    }

    if (activeSettings) {
      const body = {
        input_data: {
          ...inputData,
          ...data,
          pf_role_custom: role === 'custom' ? data.pf_role_custom : null,
          pf_cta: role === 'sales' ? data.pf_cta : null,
          pf_icp: role === 'sales' ? data.pf_icp : null,
        },
      };

      updateConfigurationSettings({
        project_id: currentProject.id,
        settings_id: activeSettings.id,
        body,
      });
    } else {
      const body = {
        prompt: {
          template_id: defaultTemplateFields.id,
          input_data: data,
        },
      };

      createConfigurationSettings({ id: currentProject.id, body });
    }
  };

  const addSalesRole = (submitData: Pick<PromptBuilderFormValues, 'pf_icp' | 'pf_cta'>) => {
    setValue('pf_icp', submitData.pf_icp);
    setValue('pf_cta', submitData.pf_cta);
    setValue('pf_role', 'sales');
  };

  const next = () => {
    navigate(getCreateProjectStepPath(id, +step + 1, projectTypeVar), { replace: true });
  };

  const back = () => {
    navigate(getCreateProjectStepPath(id, isV2 ? +step - 2 : +step - 1, projectTypeVar), {
      replace: true,
    });
  };

  return (
    <div className={'w-full flex flex-col gap-5 items-center'}>
      <form
        id={'create-project-step4'}
        className={'w-full flex flex-col gap-5'}
        onSubmit={handleSubmit(submit)}
      >
        <div className={'flex flex-col gap-2.5'}>
          <Controller
            control={control}
            name="pf_role"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <RadioGroup
                onValueChange={(val) => {
                  onChange(val);

                  if (val === 'custom') {
                    customInputRef?.current?.focus();
                  }
                }}
                value={value}
                className="flex flex-col mt-5 mb-2.5 gap-5"
              >
                {formFieldsObject?.pf_role?.options?.map((option) => {
                  const item = {
                    ...ROLE_OPTIONS[option.key],
                    ...option,
                  };

                  if (isV2 && item.value === 'custom') {
                    return null;
                  }

                  return (
                    <div key={`${item.id}-${item.key}`} className={'flex items-start gap-2.5'}>
                      <RadioGroupItem className={'mt-1'} value={item.value} id={item.value} />
                      <label htmlFor={item.value} className="cursor-pointer">
                        <p className="text-sm font-semibold text-ebony opacity-80 hover:opacity-100 transition-colors">
                          {item.title || item.key}
                        </p>
                        {item.description && (
                          <p className={'text-sm text-ebony_opacity_50 font-medium'}>
                            {item.description}
                          </p>
                        )}
                        <div className={'flex flex-row gap-2.5 mt-1'}>
                          {item.value === 'sales' && (
                            <>
                              <SalesRolePopup
                                data={inputData}
                                watch={watch}
                                addSalesRole={addSalesRole}
                                roleClickValue={role}
                                titleDescription1={
                                  'Describe your customers’ main pain points and how your solution addresses them — the AI will use this to tailor its sales conversations.'
                                }
                                titleDescription2={
                                  'Describe your customers’ main pain points and how your solution addresses them — the AI will use this to tailor its sales conversations.'
                                }
                                titleDescription3={
                                  "Set up call-to-action responses (e.g., “Let's schedule a demo session” or “Ready to kick off a free trial?”) so the AI knows how to react when buying intent is detected."
                                }
                                triggerClassName={clsx(
                                  role !== 'sales' && 'pointer-events-none text-ebony_opacity_50',
                                )}
                              />
                              {error && <p className={'text-error text-xs'}>{error.message}</p>}
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </RadioGroup>
            )}
          />
          {role === 'custom' && (
            <Controller
              control={control}
              name={'pf_role_custom'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <div className={'flex flex-col w-full relative'}>
                  <Input
                    ref={customInputRef}
                    readonly={role !== 'custom'}
                    textareaRows={3}
                    type={'textarea'}
                    autoFocus={true}
                    placeholder={
                      'Provide your AI agent’s role here. For example: Customer Support Specialist who assists users with troubleshooting.'
                    }
                    invalidMessage={error?.message}
                    value={value}
                    isValid={true}
                    onChange={onChange}
                  />
                  {error?.message && (
                    <div
                      className={
                        'absolute top-[30%] left-[calc(100%+8px)] border border-[#C11A45B2] text-[#C11A45B2] rounded-sm-5 text-xs px-3 py-[9px]'
                      }
                    >
                      <div className="absolute left-0 top-1/4 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-white border-l border-b border-[#C11A45B2]"></div>
                      {error?.message}
                    </div>
                  )}
                </div>
              )}
            />
          )}
        </div>
      </form>
      <Button
        form="create-project-step4"
        type="submit"
        className="min-w-24 w-full flex flex-row h-12"
      >
        Continue
      </Button>
      <button
        type="button"
        onClick={back}
        className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
      >
        ← Back
      </button>
    </div>
  );
};
